import { useCallback, useEffect, useState } from "react";
import { PaymentMethodVM, PaymentMethodEntityTypesEnum } from "@propertelligent/client-api";
import PaymentAccountsTopHeader from "../../../newDashboard/Pages/payment-accounts/PaymentAccountsTopHeader";
import PaymentAccountCardItem from "../../../newDashboard/Pages/payment-accounts/PaymentAccountCardItem";
import "../../../newDashboard/Pages/payment-accounts/PaymentAccountCardItem.css";
import PlaidPaymentAccountModals from "../../../newDashboard/Pages/payment-accounts/PlaidPaymentAccountModals";
import EditPlaidAccountNameModal from "../../../newDashboard/Pages/payment-accounts/EditPlaidAccountNameModal";
import TextButton from "../../../newComponents/buttons/text-button";
import { PlaidAccountDetails } from "../../firstTimeUserExperience/Context";
import { fetchMyOrganizationUserPaymentMethods } from "./organizationUserPaymentAccountsApi";

// Per product requirement: an organization user may connect up to 3 personal payment accounts (client-side
// guard; backend enforces the same cap in PaymentMethodService.EnforceOrganizationUserPaymentMethodLimitAsync).
const ORG_USER_PAYMENT_METHOD_LIMIT = 3;

// PaymentMethodEntityTypesEnum.OrganizationUser (= 5). Sent only to satisfy the PaymentMethodVM
// "EntityTypeId != 0" guard -- the backend always re-derives EntityId from the caller's own JWT
// (PaymentMethodService.CreateOrganizationUserPaymentMethodAsync), never trusting this value.
// TODO: swap to PaymentMethodEntityTypesEnum.OrganizationUser once @propertelligent/client-api is regenerated.
const ORG_USER_PM_ENTITY_TYPE_ID = 5 as PaymentMethodEntityTypesEnum;

/**
 * My Settings > Payment Accounts — an organization user's own Plaid-connected receiving account, used as a payee
 * on payables (salary/commission/reimbursement/maintenance/other). Self-managed only: no other role (including
 * Org Admin) can add/update/delete it (see PaymentMethodService.EnsureCallerOwnsOrganizationUserPaymentMethod).
 * My Settings is not permissioned (see my-settings/index.tsx), so this page has no PermissionGuard -- reachability
 * is gated one level up by OrganizationSidebar's isOrganizationUser check. PaymentAccountCardItem's own
 * canWrite("PaymentAccounts") org-module check is bypassed via alwaysAllowActions, since self-managing this
 * account is not tied to the org's PaymentAccounts module permission (which governs the *organization's* own
 * accounts, not an individual org user's personal one).
 *
 * Deliberately mirrors My Organization's Payment Accounts page (../../../newDashboard/Pages/payment-accounts) --
 * same header/list/card components and Add/Reconnect/Rename/Delete flow -- with a self-scoped list fetch (this
 * account type is never returned by the organization-scoped list, so it can't leak in there) and a lower cap.
 * Plaid-typed rows never show the legacy ACH/CreditCard "Edit" action (showEditAction=false, since this screen
 * only ever creates Plaid rows) -- renaming the connected Plaid account's name uses onRenamePlaidAccount instead.
 * onEditPaymentMethod exists only because the empty-state "Add Your First Payment Account" button invokes it.
 */
const OrganizationUserPaymentAccountsPage = () => {
  const [rows, setRows] = useState<PaymentMethodVM[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [hasAnyPaymentAccounts, setHasAnyPaymentAccounts] = useState<boolean>(false);
  const [isAddModalOpen, setAddModalOpen] = useState(false);
  const [isReconnectModalOpen, setReconnectModalOpen] = useState(false);
  const [reconnectPaymentMethod, setReconnectPaymentMethod] = useState<PaymentMethodVM | null>(null);
  const [isRenameModalOpen, setRenameModalOpen] = useState(false);
  const [renamePaymentMethod, setRenamePaymentMethod] = useState<PaymentMethodVM | null>(null);

  // A fetch failure is kept distinct from "no accounts yet" -- otherwise a user who already has accounts (up to
  // the 3-account cap) would see the empty-state "Add Your First Payment Account" prompt on a transient failure
  // and be invited into an Add flow the backend would reject anyway.
  const fetchMyPaymentMethods = useCallback(async () => {
    setLoading(true);
    try {
      const methods = await fetchMyOrganizationUserPaymentMethods();
      setRows(methods);
      setLoadError(false);
      if (methods.length > 0) {
        setHasAnyPaymentAccounts(true);
      }
    } catch (error) {
      console.error("Error loading organization user payment accounts:", error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMyPaymentMethods();
  }, [fetchMyPaymentMethods]);

  const filteredRows = searchTerm
    ? rows.filter((row) =>
        (row.accountName || "").toLowerCase().includes(searchTerm.toLowerCase())
      )
    : rows;

  const isAtPaymentMethodLimit = rows.length >= ORG_USER_PAYMENT_METHOD_LIMIT;

  // PaymentAccountCardItem drives its own infinite-scroll off this shape; the self-scoped list is small
  // (max 3) and single-page, so hasMore is always false and fetchData just re-reads the full list.
  const dataHookState = {
    rows: filteredRows,
    loading,
    hasMore: false,
    fetchData: () => fetchMyPaymentMethods(),
    currentPage: 1,
  };

  const handleAddPaymentMethod = () => {
    if (isAtPaymentMethodLimit) return;
    setAddModalOpen(true);
  };

  const handleReconnectPlaid = useCallback((paymentMethod: PaymentMethodVM) => {
    setReconnectPaymentMethod(paymentMethod);
    setReconnectModalOpen(true);
  }, []);

  const handleRenamePlaidAccount = useCallback((paymentMethod: PaymentMethodVM) => {
    setRenamePaymentMethod(paymentMethod);
    setRenameModalOpen(true);
  }, []);

  // Memoized so AddPlaidAccount's onPlaidSuccess effect (listed in its own deps) is not re-fired by a new
  // callback identity every render -- part of preventing a post-add infinite fetch loop (RightSideModal keeps
  // its children mounted, CSS-hidden, when closed).
  const handlePlaidSuccess = useCallback((_plaidData: PlaidAccountDetails) => {
    setAddModalOpen(false);
    setReconnectModalOpen(false);
    setReconnectPaymentMethod(null);
    fetchMyPaymentMethods();
  }, [fetchMyPaymentMethods]);

  return (
    <div className="payment-accounts-page content-wrapper container-fluid">
      {hasAnyPaymentAccounts && (
        <PaymentAccountsTopHeader
          PageHeading="Payment Accounts"
          // Suppress a stale count while the accounts failed to (re)load rather than showing the last-known
          // number alongside the "Unable to load" error state below.
          Count={loadError ? undefined : rows.length}
          ButtonName={!isAtPaymentMethodLimit ? "Add Payment Account" : undefined}
          MobileButtonName={!isAtPaymentMethodLimit ? "Add" : undefined}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          handleFilterChange={() => {}}
          isAddDisabled={isAtPaymentMethodLimit}
          addDisabledMessage={`You can add up to ${ORG_USER_PAYMENT_METHOD_LIMIT} payment accounts.`}
          onClick={handleAddPaymentMethod}
        />
      )}

      {loadError ? (
        <div className="payment-accounts-load-error text-center p-4">
          <p className="text-muted mb-2">Unable to load your payment accounts.</p>
          <TextButton label="Retry" onClick={() => fetchMyPaymentMethods()} />
        </div>
      ) : (
        <PaymentAccountCardItem
          dataHookState={dataHookState}
          onEditPaymentMethod={handleAddPaymentMethod}
          onReconnectPlaid={handleReconnectPlaid}
          onRenamePlaidAccount={handleRenamePlaidAccount}
          className="organisation-listing-card"
          hasAnyPaymentAccounts={hasAnyPaymentAccounts}
          // This screen only ever creates Plaid-typed rows, so the legacy ACH/CreditCard Edit action is
          // never applicable here -- renaming the connected Plaid account uses onRenamePlaidAccount above.
          showEditAction={false}
          alwaysAllowActions={true}
        />
      )}

      <PlaidPaymentAccountModals
        isAddModalOpen={isAddModalOpen}
        onAddModalClose={() => setAddModalOpen(false)}
        isReconnectModalOpen={isReconnectModalOpen}
        reconnectPaymentMethod={reconnectPaymentMethod}
        onReconnectModalClose={() => {
          setReconnectModalOpen(false);
          setReconnectPaymentMethod(null);
        }}
        onPlaidSuccess={handlePlaidSuccess}
        plaidEntityConfig={{
          // The backend derives the owning org user + payee from the caller's JWT
          // (PaymentMethodService.CreateOrganizationUserPaymentMethodAsync); this value only has to
          // satisfy the VM's "EntityTypeId != 0" guard.
          entityTypeId: ORG_USER_PM_ENTITY_TYPE_ID,
          entityId: 0,
        }}
      />

      <EditPlaidAccountNameModal
        isOpen={isRenameModalOpen}
        paymentMethod={renamePaymentMethod}
        onClose={() => {
          setRenameModalOpen(false);
          setRenamePaymentMethod(null);
        }}
        onRenamed={() => {
          setRenameModalOpen(false);
          setRenamePaymentMethod(null);
          fetchMyPaymentMethods();
        }}
      />
    </div>
  );
};

export default OrganizationUserPaymentAccountsPage;
